import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { FiHeart, FiMessageCircle, FiShare2, FiEye, FiPlay, FiX } from 'react-icons/fi';
import api from '../services/api';
import Loading from './Loading';
import './TikTokAnalyticsChart.css';

const TikTokAnalyticsChart = ({ accounts }) => {
  const [posts, setPosts] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('view_count');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [timeRange, setTimeRange] = useState(7);
  const [loading, setLoading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    loadPosts();
  }, [selectedAccount, timeRange, accounts]);

  const getApiBase = () => {
    const baseURL = api?.defaults?.baseURL || '/api/content';
    return baseURL.replace(/\/api\/content\/?$/i, '/api/tiktok');
  };

  const loadPosts = async () => {
    if (!accounts || accounts.length === 0) return;

    setLoading(true);
    try {
      const API_BASE_URL = getApiBase();

      // Si está en "all", NO pasar account_id para obtener todas las cuentas
      const url = selectedAccount === 'all'
        ? `${API_BASE_URL}/tracked-accounts/posts/?days=${timeRange}`
        : `${API_BASE_URL}/tracked-accounts/posts/?account_id=${selectedAccount}&days=${timeRange}`;

      const response = await api.get(url);

      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error cargando posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString('es-CL');
  };

  const metrics = [
    { id: 'view_count', label: 'Views', icon: FiEye, color: '#3b82f6' },
    { id: 'like_count', label: 'Likes', icon: FiHeart, color: '#ef4444' },
    { id: 'comment_count', label: 'Comentarios', icon: FiMessageCircle, color: '#10b981' },
    { id: 'share_count', label: 'Shares', icon: FiShare2, color: '#f59e0b' },
  ];

  // Agrupar posts por video_id
  const groupedByVideo = posts.reduce((acc, post) => {
    if (!acc[post.video_id]) {
      acc[post.video_id] = {
        video_id: post.video_id,
        caption: post.caption,
        video_url: post.video_url,
        video_cover: post.video_cover,
        data: []
      };
    }
    acc[post.video_id].data.push({
      date: post.snapshot_timestamp,
      like_count: post.like_count,
      comment_count: post.comment_count,
      share_count: post.share_count,
      view_count: post.view_count,
    });
    return acc;
  }, {});

  const videos = Object.values(groupedByVideo);
  const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

  // Calcular totales usando solo el último registro de cada video
  const getLatestTotals = () => {
    return videos.reduce((totals, video) => {
      // Obtener el último registro de este video
      const latest = video.data[video.data.length - 1];
      if (latest) {
        totals.like_count += latest.like_count || 0;
        totals.comment_count += latest.comment_count || 0;
        totals.share_count += latest.share_count || 0;
        totals.view_count += latest.view_count || 0;
      }
      return totals;
    }, {
      like_count: 0,
      comment_count: 0,
      share_count: 0,
      view_count: 0
    });
  };

  const latestTotals = getLatestTotals();

  if (loading) {
    return <Loading fullScreen={false} />;
  }

  if (posts.length === 0) {
    return (
      <div className="analytics-empty">
        <FiEye size={48} />
        <h3>No hay datos de videos</h3>
        <p>Los datos se actualizan cada 10 minutos automáticamente</p>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <div className="header-title-section">
          <h2>📊 Analytics de TikTok</h2>
        </div>
        <div className="analytics-controls">
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="analytics-select"
          >
            <option value="all">Todas las cuentas</option>
            {accounts.map(acc => (
              <option key={acc.id} value={acc.id}>@{acc.username}</option>
            ))}
          </select>

          <select
            value={timeRange}
            onChange={(e) => setTimeRange(parseInt(e.target.value))}
            className="analytics-select"
          >
            <option value={1}>Último día</option>
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
          </select>
        </div>
      </div>

      <div className="metrics-selector">
        {metrics.map(metric => {
          const Icon = metric.icon;
          return (
            <button
              key={metric.id}
              className={`metric-btn ${selectedMetric === metric.id ? 'active' : ''}`}
              onClick={() => setSelectedMetric(metric.id)}
            >
              <Icon size={18} />
              {metric.label}
            </button>
          );
        })}
      </div>

      <div className="stats-overview">
        {metrics.map(metric => {
          const Icon = metric.icon;
          const total = latestTotals[metric.id] || 0;

          return (
            <div
              key={metric.id}
              className="stat-overview-card"
              style={{ borderLeftColor: metric.color }}
            >
              <div className="stat-overview-header">
                <span className="stat-overview-username">
                  <Icon size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  {metric.label}
                </span>
              </div>
              <div className="stat-overview-value">{formatNumber(total)}</div>
            </div>
          );
        })}
      </div>

      <div className="chart-container">
        <h3>
          {metrics.find(m => m.id === selectedMetric)?.label} por Video
          <span style={{ fontSize: '0.875rem', color: '#6b7280', marginLeft: '1rem', fontWeight: '400' }}>
            {videos.length} video(s) con {posts.length} mediciones
          </span>
        </h3>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              type="category"
              allowDuplicatedCategory={false}
              tickFormatter={formatDate}
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              tickFormatter={formatNumber}
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <Tooltip
              labelFormatter={formatDate}
              formatter={(value) => formatNumber(value)}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px'
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              formatter={(value) => {
                const video = videos.find(v => v.video_id === value);
                return video ? (video.caption?.slice(0, 40) + '...' || `Video ${value.slice(0, 8)}`) : value;
              }}
            />

            {videos.map((video, idx) => (
              <Line
                key={video.video_id}
                data={video.data}
                dataKey={selectedMetric}
                name={video.video_id}
                stroke={colors[idx % colors.length]}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="video-details">
        <h3>Detalles de Videos</h3>
        <div className="video-list">
          {videos.map((video, idx) => (
            <div key={video.video_id} className="video-item">
              <div
                className="video-color"
                style={{ backgroundColor: colors[idx % colors.length] }}
              />
              <div className="video-info">
                <div className="video-caption">{video.caption || `Video ${video.video_id.slice(0, 12)}`}</div>
                <div className="video-stats">
                  <span><FiEye size={14} /> {formatNumber(video.data[video.data.length - 1]?.view_count || 0)}</span>
                  <span><FiHeart size={14} /> {formatNumber(video.data[video.data.length - 1]?.like_count || 0)}</span>
                  <span><FiMessageCircle size={14} /> {formatNumber(video.data[video.data.length - 1]?.comment_count || 0)}</span>
                  <span><FiShare2 size={14} /> {formatNumber(video.data[video.data.length - 1]?.share_count || 0)}</span>
                </div>
                <div className="video-measurements">{video.data.length} mediciones</div>
              </div>
              {video.video_url && (
                <button
                  className="video-play-btn"
                  onClick={() => setSelectedVideo(video)}
                  title="Ver video"
                >
                  <FiPlay size={20} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Modal para ver video */}
      {selectedVideo && (
        <div className="video-modal-overlay" onClick={() => setSelectedVideo(null)}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" onClick={() => setSelectedVideo(null)}>
              <FiX size={24} />
            </button>
            <div className="video-modal-header">
              <h3>{selectedVideo.caption || `Video ${selectedVideo.video_id.slice(0, 12)}`}</h3>
            </div>
            <div className="video-modal-body">
              <iframe
                src={selectedVideo.video_url}
                width="100%"
                height="600"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={selectedVideo.caption || 'TikTok Video'}
              />
            </div>
            <div className="video-modal-footer">
              <a
                href={selectedVideo.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="video-modal-link"
              >
                Abrir en TikTok
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TikTokAnalyticsChart;
